import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../ui/components/Button";
import { Dialog } from "../../../ui/components/Dialog";
import { IconButton } from "../../../ui/components/IconButton";
import { TextArea } from "../../../ui/components/TextArea";
import {
  FeatherAlertTriangle,
  FeatherArrowRight,
  FeatherCheck,
  FeatherChevronLeft,
  FeatherFileText,
  FeatherInfo,
  FeatherMessageSquare,
  FeatherX,
} from "@subframe/core";
import {
  createReviewRequest,
  type ChangeDetails,
  type CreateReviewRequestDTO,
} from "../../utils/reviewWorkflow";
import { useToast } from "./Toast";
import { useTaxes } from "../hooks/useTaxes";
import {
  buildInvoiceBillReviewChangesViewModel,
  type FieldChangeRow,
} from "../../utils/review/buildInvoiceBillReviewChangesViewModel";

export interface InvoiceBillReviewChangesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleCode: string;
  actionCode: string;
  entityType: "invoice" | "bill";
  entityId: number;
  documentNumber?: string;
  changeDetails?: ChangeDetails;
  requiresReview: boolean;
  onSuccess?: (comments?: string) => void;
  onError?: (error: Error) => void;
  disableSubmit?: boolean;
  skipNavigation?: boolean;
  title?: string;
}

function ChangeDiffRow({ row }: { row: FieldChangeRow }) {
  const hasOld = row.oldText.trim() !== "" && row.oldText !== "—";
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <span className="text-body font-body text-subtext-color">
        {row.label}
      </span>
      <div className="flex items-center gap-2">
        {hasOld ? (
          <>
            <span className="text-body font-body text-neutral-400 line-through">
              {row.oldText}
            </span>
            <FeatherArrowRight className="text-caption font-caption text-neutral-400" />
          </>
        ) : null}
        <span className="text-body-bold font-body-bold text-[#1d4ed8] rounded-md bg-blue-50 px-2 py-0.5">
          {row.newText}
        </span>
      </div>
    </div>
  );
}

function TotalImpactRow({
  row,
  emphasized,
}: {
  row: FieldChangeRow;
  emphasized?: boolean;
}) {
  const hasOld = row.oldText.trim() !== "" && row.oldText !== "—";
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <span
        className={
          emphasized
            ? "text-body-bold font-body-bold text-default-font"
            : "text-body font-body text-subtext-color"
        }
      >
        {row.label}
      </span>
      <div className="flex items-center gap-2">
        {hasOld ? (
          <>
            <span className="text-body font-body text-neutral-400 line-through">
              {row.oldText}
            </span>
            <FeatherArrowRight className="text-caption font-caption text-neutral-400" />
          </>
        ) : null}
        <span
          className={
            emphasized
              ? "text-heading-3 font-heading-3 text-[#1d4ed8] rounded-md bg-blue-100 px-2 py-0.5"
              : "text-body-bold font-body-bold text-default-font"
          }
        >
          {row.newText}
        </span>
      </div>
    </div>
  );
}

export const InvoiceBillReviewChangesModal: React.FC<
  InvoiceBillReviewChangesModalProps
> = ({
  open,
  onOpenChange,
  moduleCode,
  actionCode,
  entityType,
  entityId,
  documentNumber,
  changeDetails,
  requiresReview,
  onSuccess,
  onError,
  disableSubmit = false,
  skipNavigation = false,
  title,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError: showToastError } = useToast();
  const { taxes } = useTaxes();
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const successShownRef = useRef(false);

  const kind = moduleCode?.toLowerCase() === "bill" ? "bill" : "invoice";

  const viewModel = useMemo(
    () =>
      buildInvoiceBillReviewChangesViewModel({
        kind,
        changeDetails,
        taxes,
        requiresReview,
        actionCode,
      }),
    [kind, changeDetails, taxes, requiresReview, actionCode],
  );

  useEffect(() => {
    if (open) successShownRef.current = false;
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) {
      setComments("");
      setError(null);
      onOpenChange(false);
    }
  };

  const handleSubmit = async () => {
    if (!comments.trim()) {
      setError("Comment is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!requiresReview) {
        onSuccess?.(comments.trim());
        handleClose();
        return;
      }

      const requestData: CreateReviewRequestDTO = {
        module_code: moduleCode,
        action_code: actionCode,
        entity_type: entityType,
        entity_id: entityId,
        request_comments: comments.trim(),
        change_details: changeDetails,
      };

      await createReviewRequest(requestData);
      if (!successShownRef.current) {
        successShownRef.current = true;
        showSuccess("Review request submitted successfully");
      }

      onSuccess?.(comments.trim());
      handleClose();

      if (!skipNavigation) {
        setTimeout(() => {
          navigate(-1);
        }, 100);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create review request";
      setError(errorMessage);
      showToastError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionTypesRaw =
    changeDetails?.action_types && changeDetails.action_types.length > 0
      ? changeDetails.action_types
      : actionCode
        ? [actionCode]
        : [];

  const isActionOnlyReview =
    requiresReview &&
    viewModel.isEmpty &&
    actionTypesRaw.some((a) =>
      ["reject", "duplicate", "delete_after_push_to_zoho", "create"].includes(
        a,
      ),
    );

  const resolvedTitle =
    title ??
    (isActionOnlyReview ? "Request Review" : "Review changes before approving");

  const documentLabel = entityType === "bill" ? "bill" : "invoice";
  const detailsSectionLabel =
    entityType === "bill" ? "Bill details" : "Invoice details";

  const statsParts: string[] = [];
  if (viewModel.stats.changeCount > 0) {
    statsParts.push(
      `${viewModel.stats.changeCount} change${viewModel.stats.changeCount === 1 ? "" : "s"} made`,
    );
  }
  if (viewModel.stats.affectedLineItems > 0) {
    statsParts.push(
      `${viewModel.stats.affectedLineItems} line item${viewModel.stats.affectedLineItems === 1 ? "" : "s"} affected`,
    );
  }
  if (viewModel.stats.totalDifferenceFormatted) {
    statsParts.push(
      `${viewModel.stats.totalDifferenceFormatted} total difference`,
    );
  }

  const hasChangeContent =
    !viewModel.isEmpty ||
    viewModel.actionTypes.length > 0 ||
    Boolean(changeDetails?.action_types?.length);

  const primaryCta = requiresReview
    ? isSubmitting
      ? "Submitting..."
      : "Submit for Review"
    : isSubmitting
      ? "Approving..."
      : "Approve with changes";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <Dialog.Content className="min-w-[320px] grow shrink-0 basis-0 w-[640px] max-w-[90vw] max-h-[96vh] rounded-[20px] border border-solid border-neutral-100 bg-default-background shadow-lg overflow-hidden p-0">
        <div className="flex w-full min-h-0 flex-1 flex-col items-start max-h-[96vh]">
          <div className="flex w-full items-start gap-4 px-6 pt-6 pb-5">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#fffbeb]">
              <FeatherAlertTriangle className="text-heading-3 font-heading-3 text-[#f59e0b]" />
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 pt-0.5">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {resolvedTitle}
              </span>
              <span className="text-body font-body text-subtext-color">
                {requiresReview
                  ? `Some values were updated. Review the changes before submitting this ${documentLabel} for approval.`
                  : `Some OCR-extracted values were updated. Review the changes before approving this ${documentLabel}.`}
              </span>
            </div>
            <IconButton
              size="small"
              icon={<FeatherX />}
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          {error ? (
            <div className="w-full px-6 pb-3">
              <div className="w-full rounded-md bg-error-50 border border-error-200 px-4 py-3">
                <span className="text-body font-body text-error-700">
                  {error}
                </span>
              </div>
            </div>
          ) : null}

          {(changeDetails?.closed_month === true ||
            changeDetails?.action_types?.includes("month_close")) && (
            <div className="w-full px-6 pb-3">
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-100 px-4 py-3 border border-solid border-neutral-200">
                <span className="text-body-bold font-body-bold text-neutral-700">
                  Closed month
                </span>
                <span className="text-body font-body text-neutral-700">
                  This request is for a change in a closed month and requires
                  review and approval.
                </span>
              </div>
            </div>
          )}

          {requiresReview && viewModel.actionTypes.length > 0 ? (
            <div className="w-full px-6 pb-3">
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-info-50 px-4 py-3 border border-solid border-info-200">
                <span className="text-body-bold font-body-bold text-info-700">
                  Action Request
                </span>
                <div className="flex flex-col items-start gap-1">
                  {viewModel.actionTypes.map((action, index) => (
                    <span
                      key={index}
                      className="text-body font-body text-info-700"
                    >
                      • {action}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {statsParts.length > 0 && !viewModel.isEmpty ? (
            <div className="flex w-full items-center gap-3 border-t border-solid border-neutral-100 bg-neutral-50 px-6 py-3 flex-wrap">
              {statsParts.map((part, index) => (
                <React.Fragment key={part}>
                  {index > 0 ? (
                    <div className="flex h-1 w-1 flex-none items-start rounded-full bg-neutral-300" />
                  ) : null}
                  <span
                    className={
                      index === 0
                        ? "text-caption-bold font-caption-bold text-default-font"
                        : "text-caption font-caption text-subtext-color"
                    }
                  >
                    {part}
                  </span>
                </React.Fragment>
              ))}
            </div>
          ) : null}

          <div className="flex w-full flex-1 flex-col min-h-0 overflow-auto">
            <div className="flex w-full min-h-full flex-col gap-6 px-6 pt-6 pb-6">
              {disableSubmit ? (
                <div className="flex w-full flex-col items-start gap-2 rounded-md bg-warning-50 px-4 py-3 border border-solid border-warning-200">
                  <span className="text-body-bold font-body-bold text-warning-700">
                    No Changes Detected
                  </span>
                  <span className="text-body font-body text-warning-700">
                    Please make changes to the document before requesting a
                    review.
                  </span>
                </div>
              ) : null}

              {viewModel.headerChanges.length > 0 ? (
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wide">
                    {detailsSectionLabel}
                  </span>
                  <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-100 px-4 py-3.5">
                    {viewModel.headerChanges.map((row) => (
                      <ChangeDiffRow key={row.label} row={row} />
                    ))}
                  </div>
                </div>
              ) : null}

              {viewModel.lineItemChanges.length > 0 ? (
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wide">
                    Line item changes
                  </span>
                  {viewModel.lineItemChanges.map((group) => (
                    <div
                      key={group.lineNumber}
                      className="flex w-full flex-col items-start rounded-lg border border-solid border-neutral-100"
                    >
                      <div className="flex w-full min-w-0 items-center gap-2 border-b border-solid border-neutral-100 px-4 py-2.5">
                        <FeatherFileText className="text-body font-body text-subtext-color flex-none" />
                        <span
                          className="min-w-0 flex-1 truncate text-body-bold font-body-bold text-default-font"
                          title={`Line item ${group.lineNumber}: ${group.title}`}
                        >
                          Line item {group.lineNumber}: {group.title}
                        </span>
                      </div>
                      {group.changes.map((row, idx) => (
                        <div
                          key={`${row.label}-${idx}`}
                          className={`flex w-full items-center justify-between px-4 py-2.5 gap-3 ${
                            idx < group.changes.length - 1
                              ? "border-b border-solid border-neutral-100"
                              : ""
                          }`}
                        >
                          <ChangeDiffRow row={row} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : null}

              {viewModel.glOverrideSections.length > 0 ? (
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wide">
                    Permanent GL override
                  </span>
                  {viewModel.glOverrideSections.map((group) => (
                    <div
                      key={group.lineNumber}
                      className="flex w-full flex-col items-start rounded-lg border border-solid border-neutral-100 overflow-hidden"
                    >
                      <div className="flex w-full items-center gap-2 border-b border-solid border-neutral-100 bg-amber-50/80 px-4 py-2.5">
                        <FeatherFileText className="text-body font-body text-amber-700" />
                        <span className="text-body-bold font-body-bold text-default-font">
                          {group.title}
                        </span>
                        <span className="text-caption font-caption text-subtext-color">
                          · Line {group.lineNumber}
                        </span>
                      </div>
                      <div className="flex w-full items-center justify-between border-b border-solid border-neutral-100 px-4 py-2.5 gap-3">
                        <ChangeDiffRow row={group.accountChange} />
                      </div>
                      {group.reason ? (
                        <div className="flex w-full flex-col items-start gap-2 bg-neutral-50 px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <FeatherMessageSquare className="text-caption font-caption text-subtext-color" />
                            <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wide">
                              Reason for GL override
                            </span>
                          </div>
                          <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-200 bg-white px-3.5 py-3">
                            <span className="text-body font-body text-default-font whitespace-pre-line leading-relaxed">
                              {group.reason}
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {(viewModel.totalImpact.subTotal ||
                viewModel.totalImpact.taxTotal ||
                viewModel.totalImpact.grandTotal ||
                viewModel.totalImpact.tcsTotal) && (
                <div className="flex w-full flex-col items-start gap-3">
                  <span className="text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wide">
                    Total impact
                  </span>
                  <div className="flex w-full flex-col items-start rounded-lg border border-solid border-[#dbeafe] bg-[#eff6ff66]">
                    {viewModel.totalImpact.subTotal ? (
                      <div className="flex w-full border-b border-solid border-[#dbeafe] px-4 py-2.5">
                        <TotalImpactRow row={viewModel.totalImpact.subTotal} />
                      </div>
                    ) : null}
                    {viewModel.totalImpact.taxTotal ? (
                      <div className="flex w-full border-b border-solid border-[#dbeafe] px-4 py-2.5">
                        <TotalImpactRow row={viewModel.totalImpact.taxTotal} />
                      </div>
                    ) : null}
                    {viewModel.totalImpact.tcsTotal ? (
                      <div className="flex w-full border-b border-solid border-[#dbeafe] px-4 py-2.5">
                        <TotalImpactRow row={viewModel.totalImpact.tcsTotal} />
                      </div>
                    ) : null}
                    {viewModel.totalImpact.grandTotal ? (
                      <div className="flex w-full px-4 py-3">
                        <TotalImpactRow
                          row={viewModel.totalImpact.grandTotal}
                          emphasized
                        />
                      </div>
                    ) : null}
                  </div>
                  {viewModel.totalImpact.reverseCharge?.show ? (
                    <div className="flex w-full items-start gap-2 rounded-md border border-solid border-info-200 bg-info-50 px-3 py-2">
                      <FeatherInfo className="h-4 w-4 shrink-0 text-info-700" />
                      <span className="text-body font-body text-info-700">
                        Reverse charges of{" "}
                        {viewModel.totalImpact.reverseCharge.amountFormatted}{" "}
                        will be applied
                      </span>
                    </div>
                  ) : null}
                  {viewModel.showRecalculatedNote ? (
                    <div className="flex items-center gap-1.5">
                      <FeatherInfo className="text-caption font-caption text-subtext-color" />
                      <span className="text-caption font-caption text-subtext-color">
                        Totals were recalculated based on your edits.
                      </span>
                    </div>
                  ) : null}
                </div>
              )}

              {!hasChangeContent && !disableSubmit && requiresReview ? (
                <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200">
                  <span className="text-body font-body text-subtext-color">
                    {documentNumber
                      ? `Reviewing ${documentLabel} #${documentNumber}`
                      : `Reviewing ${documentLabel} #${entityId}`}
                  </span>
                </div>
              ) : null}

              <TextArea
                className="mt-auto h-auto w-full flex-none"
                variant="filled"
                label={
                  <>
                    Comments <span className="text-[#dc2626]">*</span>
                  </>
                }
                helpText={
                  requiresReview
                    ? "Please explain why this change requires review"
                    : "Please explain why you are approving these changes"
                }
                error={!!error && !comments.trim()}
              >
                <TextArea.Input
                  placeholder="Enter your comments here... (Required)"
                  value={comments}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setComments(event.target.value);
                    setError(null);
                  }}
                  rows={4}
                />
              </TextArea>
            </div>
          </div>

          <div className="flex w-full flex-none items-center justify-end gap-2 border-t border-solid border-neutral-100 bg-default-background px-6 py-4">
            <Button
              variant="neutral-secondary"
              icon={requiresReview ? undefined : <FeatherChevronLeft />}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {requiresReview ? "Cancel" : "Review again"}
            </Button>
            <Button
              icon={requiresReview ? undefined : <FeatherCheck />}
              onClick={handleSubmit}
              disabled={isSubmitting || disableSubmit || !comments.trim()}
            >
              {primaryCta}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default InvoiceBillReviewChangesModal;
